import { Locale } from '@/constants/Locale';

/** `Date` -> `'YYYY-MM-DD'` in the viewer's local calendar day (never UTC-shifted). */
export const toIsoDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * `'YYYY-MM-DD'` -> `Date` at the start of that day in the viewer's local calendar, the inverse of
 * `toIsoDate`. `new Date('YYYY-MM-DD')` reads the string as UTC midnight, which is still the day
 * before anywhere west of Greenwich. Undefined for a string that is not a full date.
 */
export const parseIsoDate = (isoDate: string): Date | undefined => {
    const [year, month, day] = isoDate.split('-').map(Number);
    return year && month && day ? new Date(year, month - 1, day) : undefined;
};

/**
 * The calendar day a server date names, in the viewer's own calendar. A field the server keeps as a
 * day -- a cycle's end, never the instant it was written -- still travels as a timestamp,
 * and read as one it slides to the day before for every reader west of the offset it was written
 * in. The day the value carries is the day it means, so that day is what is read.
 */
export const parseServerDay = (serverDate: string): Date | undefined =>
    parseIsoDate(serverDate.split('T')[0]);

/**
 * The moment a server timestamp names, for a field the server keeps as an instant -- an evaluation's
 * date is stamped when it is created or picked -- so its day is the viewer's day of that moment: 8:27
 * p.m. in Montreal is already the next day in UTC. Undefined for a value that names no moment.
 */
export const parseServerInstant = (serverInstant: string): Date | undefined => {
    const instant = new Date(serverInstant);
    return Number.isNaN(instant.getTime()) ? undefined : instant;
};

/** Minutes east of UTC (e.g. 330 for IST). Inverse of <code>Date.getTimezoneOffset()</code>. */
export const getUtcOffsetMinutes = (): number => -new Date().getTimezoneOffset();

/** Maps the app's i18n language to the two language codes the `DateInput` component supports. */
export const toDateInputLang = (language: string): 'en' | 'fr' =>
    language === Locale.EN ? 'en' : 'fr';

/** Maps the app's i18n language to the Canadian locale `Intl` formats dates and times with. */
export const toCalendarLocale = (language: string): string =>
    language.startsWith('fr') ? 'fr-CA' : 'en-CA';

/** The time of day of `date`, written the way the reader's language writes it: "10 h 13" in
 * French, "10:13 a.m." in English. */
export const formatTimeOfDay = (date: Date, language: string): string =>
    date.toLocaleTimeString(toCalendarLocale(language), { hour: 'numeric', minute: '2-digit' });
